'use strict';
import { editUserDetails } from '@api/user.js';
import { AICommandsPlugin } from '@components/Comment/AICommandsPlugin.js';
// import { MentionPlugin } from '@components/Comment/MentionPlugin.js';
// import { ContextPlugin } from '@components/Comment/ContextPlugin.js';
import UICommentText from '@components/Comment/UICommentText.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import Loader from '@components/Loading/Loader.js';
import { SpatialVisionProviderSelector } from '@components/SpatialVision/SpatialVisionProviderSelector.js';
import { IS_APP, IS_IOS, IS_MOBILE } from '@constants/global.js';
import {
  AiSparkIcon,
  ArrowUpIcon,
  BookmarkIcon,
  FileIcon,
  HistoryIcon,
  ProjectsIcon,
} from '@icons/index.js';
import UIIcon from '@icons/UIIcon.js';
import {
  VLM_MAX_IMAGE_SIZE_MB,
  oversizedVlmImageNotice,
  partitionVlmImages,
} from '@pages/SpatialVision/utils/imageUploadLimits.js';
import { route } from '@router/index.js';
import FoldersState from '@states/FoldersState.js';
import ProjectsState, { AiSelectedItems } from '@states/Projects.js';
import { SpatialVisionState } from '@states/SpatialVision.js';
import UserState from '@states/User.js';
import { CadFileState } from '@states/Viewer.js';
import { UIButton, UIDiv, UIInput, UISelect, UIText } from '@ui';
import { getSharingId } from '@utils/projectUtils.js';
import { getCurrentSpaceCredits } from '@utils/userUtils.js';
import { AiAnalyzerDetectPopover } from './AiAnalyzerDetectPopover.js';
import { AiAnalyzerFilesPopover } from './AiAnalyzerFilesPopover.js';
import { AiAnalyzerProjectsPopover } from './AiAnalyzerProjectsPopover.js';

class AiAnalyzerReportSection extends UIDiv {
  constructor(
    onCreateReport,
    onToggleHistory = null,
    onToggleBookmarks = null,
    onLocalCommandResponse = null,
    onModeChange = null,
    onTypingStart = null,
    onDetectSelectionChange = null,
  ) {
    super();
    this.addClass(
      'relative flex flex-col bg-white border border-gray-200 rounded-md space-y-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all mb-1',
    );

    // AI Mode state: 'work' for project management, 'detect' for vision.
    // The legacy 'chat' mode remains supported internally but hidden in the UI.
    this.currentMode = 'detect';
    this.onModeChange = onModeChange;
    this.onTypingStart = onTypingStart;
    this.onDetectSelectionChange = onDetectSelectionChange;
    this.hasStartedTyping = false;
    this.detectSelection = {
      runMode: 'direct',
      image: null,
      images: [],
      sequenceId: null,
      sequenceName: null,
    };
    this.spatialVisionUnsubscribe = SpatialVisionState.subscribe(() => {
      this.detectProviderControl?.syncValue?.();
      this.#updateDetectSelectionUI();
      this.#updateSendButtonState();
    });
    SpatialVisionState.load().catch(error => {
      console.error('Failed to load Spatial Vision data:', error);
    });

    // Voice input is intentionally disabled in AI Analyzer.
    this.speechRecognitionSupported = false;

    document.addEventListener('iosSpeechFlag', e => {
      const detail = e?.detail;
      const text = typeof detail === 'string' ? detail : detail?.text || '';
      if (typeof text === 'string') {
        this.textarea.setValue(text);
        this.#updateSendButtonState();
      }
    });
    this.recognition = null;
    this.isRecording = false;

    // Mode badge container
    this.modeBadgeContainer = new UIDiv();
    this.modeBadgeContainer.addClass('px-3 pt-2');
    this.#buildModeBadge();
    this.add(this.modeBadgeContainer);

    this.textareaContainer = new UIDiv();
    this.textareaContainer.addClass('relative flex-grow px-3');
    this.add(this.textareaContainer);

    this.detectSequenceMessage = new UIText('');
    this.detectSequenceMessage.setClass('hidden');
    this.add(this.detectSequenceMessage);

    this.textarea = new UICommentText();
    this.textarea.setPlaceholder(
      this.#translatePlaceholder(
        'sidebar.aiAnalyzer.textarea_placeholder',
        'Type commands (/)',
      ),
    );
    // Add task suggestion plugin
    // this.taskPlugin = new ContextPlugin(this.textarea, {
    //   trigger: '#',
    // });
    // this.textarea.addPlugin(this.taskPlugin);
    // Removed until we have an use case for this one
    // this.mentionPlugin = new MentionPlugin(this.textarea, {
    //   trigger: '@',
    // });
    // this.textarea.addPlugin(this.mentionPlugin);

    // Add AI suggestion plugin
    this.aiPlugin = new AICommandsPlugin(this.textarea, {
      trigger: '/',
      onLocalCommandResponse: onLocalCommandResponse,
    });
    this.textarea.addPlugin(this.aiPlugin);
    this.textarea.addClass(
      [
        'w-full py-2',
        'bg-white',
        'border-none outline-none focus:outline-none focus:ring-0 focus:border-none',
        'resize-none overflow-auto',
        'disabled:cursor-not-allowed',
        'max-h-[30vh]', // Maximum height of 30% of viewport height
      ].join(' '),
    );
    this.textarea.setValue('');
    this.textarea.onKeyDown(e => {
      e.stopPropagation();
      if (e.keyCode === 13 && !IS_MOBILE()) {
        if (e.shiftKey) return;
        if (!this.#hasEnoughCredits(2)) {
          const credits = getCurrentSpaceCredits();
          toast.warning(
            i18next.t('sidebar.aiAnalyzer.toast.insufficient_credits'),
            i18next.t('sidebar.aiAnalyzer.toast.insufficient_credits_text', {
              credits,
            }),
          );
          if (window.webkit?.messageHandlers?.buttonPressed) {
            window.webkit.messageHandlers.buttonPressed.postMessage('clicked');
          } else if (window.AndroidInterface?.buttonPressed) {
            // Android handler
            window.AndroidInterface.buttonPressed('clicked');
          }
          return;
        }

        // Check if the text contains a local command
        const prompt = this.getPrompt();
        const localCommand =
          this.currentMode === 'detect'
            ? null
            : this.findCommand(prompt, { localOnly: true });

        if (localCommand) {
          const response = this.aiPlugin.handleLocalCommand(localCommand);
          if (response && onLocalCommandResponse) {
            // Clear the textarea and trigger the response callback
            this.clearPrompt();
            onLocalCommandResponse(localCommand.command, response);
            this.focusInput();
            return;
          }
        }

        onCreateReport();
      }
    });

    // Add auto-resize functionality
    this.textarea.onInput(() => {
      this.#updateSendButtonState();

      // Trigger onTypingStart callback only once when user starts typing
      if (!this.hasStartedTyping && this.textarea.getValue().trim() !== '') {
        this.hasStartedTyping = true;
        if (this.onTypingStart) {
          this.onTypingStart(true);
        }
      } else if (
        this.hasStartedTyping &&
        this.textarea.getValue().trim() === ''
      ) {
        this.hasStartedTyping = false;
        if (this.onTypingStart) {
          this.onTypingStart(false);
        }
      }
    });

    this.textareaContainer.add(this.textarea);

    // Create bottom controls container
    this.controlsContainer = new UIDiv();
    const controlsContainer = this.controlsContainer;
    controlsContainer.addClass(
      'flex justify-between items-center mt-0 px-3 pb-2',
    );
    this.add(controlsContainer);

    // Left side - file indicator and action icons
    this.leftControls = new UIDiv();
    const leftControls = this.leftControls;
    leftControls.addClass('flex items-center gap-2');

    this.workspaceControls = new UIDiv();
    this.workspaceControls.addClass('flex items-center gap-2');

    const projectPopover = this.#buildProjectPopover();
    this.workspaceControls.add(projectPopover);

    // Add file selection indicator
    const fileIndicator = new UIButton();
    fileIndicator.addClass(
      'p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm text-slate-600 flex items-center gap-1 transition-colors',
    );

    const fileIcon = new FileIcon({ size: 16, color: 'none' });
    fileIndicator.add(fileIcon);

    this.fileCount = new UIText(`${FoldersState.getFileCount()}`);
    fileIndicator.add(this.fileCount);

    // Subscribe to UI updates only
    FoldersState.subscribe(() => {
      this.#updateFileCountUIValue();
    });

    AiSelectedItems.subscribe(() => {
      this.updateProjectCountUIValue();
    });

    const popoverContent = new AiAnalyzerFilesPopover();
    fileIndicator.onClick(e => {
      e.stopPropagation();
      // Open the file
      Popover.show({
        trigger: fileIndicator,
        content: popoverContent,
        placement: 'top-end',
        hideOnSelect: false,
      });
    });
    this.workspaceControls.add(fileIndicator);

    this.detectControls = this.#buildDetectControls();

    leftControls.add(this.workspaceControls, this.detectControls);

    // Add history icon
    if (onToggleHistory) {
      this.historyButton = new UIButton();
      const historyButton = this.historyButton;
      historyButton.addClass(
        'p-2 hover:bg-slate-100 rounded-full transition-colors',
      );
      const historyIcon = new HistoryIcon({ size: 16, color: 'none' });
      historyButton.add(historyIcon);
      historyButton.onClick(() => onToggleHistory());
      leftControls.add(historyButton);
    }

    // Add bookmark icon
    if (onToggleBookmarks) {
      this.bookmarkButton = new UIButton();
      const bookmarkButton = this.bookmarkButton;
      bookmarkButton.addClass(
        'p-2 hover:bg-slate-100 rounded-full transition-colors',
      );
      const bookmarkIcon = new BookmarkIcon({ size: 16, color: 'none' });
      bookmarkButton.add(bookmarkIcon);
      bookmarkButton.onClick(() => onToggleBookmarks());
      leftControls.add(bookmarkButton);
    }

    controlsContainer.add(leftControls);
    this.rightControls = new UIDiv();
    const rightControls = this.rightControls;
    rightControls.addClass('flex items-center gap-2');

    // Right side - send button
    this.sendBtn = new UIButton();
    this.sendBtn.setId('send-button');
    this.sendBtn.setDisabled(true);

    // Create the arrow icon
    this.arrowIcon = new ArrowUpIcon({ size: 24, color: 'none' });

    this.sendBtn.add(this.arrowIcon);
    this.sendLabel = new UIText('');
    this.sendLabel.addClass('hidden');
    this.sendBtn.add(this.sendLabel);

    // Create the loading spinner
    this.loadingSpinner = new Loader(
      'w-[24px] h-[24px] border-2 border-white border-t-transparent rounded-full animate-spin',
      true,
    );
    this.loadingSpinner.hide();
    this.sendBtn.add(this.loadingSpinner);

    this.sendBtn.setClass(
      [
        'p-2 bg-[var(--o-hive-primary-80)] text-white rounded-full',
        'hover:bg-[var(--o-hive-primary-100)] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors',
        'disabled:bg-gray-400 disabled:cursor-not-allowed',
      ].join(' '),
    );

    this.sendBtn.onClick(async e => {
      e.stopPropagation();
      if (!this.#hasEnoughCredits(2)) {
        const credits = getCurrentSpaceCredits();
        toast.warning(
          i18next.t('sidebar.aiAnalyzer.toast.insufficient_credits'),
          i18next.t('sidebar.aiAnalyzer.toast.insufficient_credits_text', {
            credits,
          }),
        );
        if (window.webkit?.messageHandlers?.buttonPressed) {
          window.webkit.messageHandlers.buttonPressed.postMessage('clicked');
        } else if (window.AndroidInterface?.buttonPressed) {
          window.AndroidInterface.buttonPressed('clicked');
        }
        return;
      }

      // Check if the text contains a local command
      const prompt = this.getPrompt();
      const localCommand =
        this.currentMode === 'detect'
          ? null
          : this.findCommand(prompt, { localOnly: true });

      if (localCommand) {
        const response = this.aiPlugin.handleLocalCommand(localCommand);
        if (response && onLocalCommandResponse) {
          // Clear the textarea and trigger the response callback
          this.clearPrompt();
          onLocalCommandResponse(localCommand.command, response);
          this.focusInput();
          return;
        }
      }

      await onCreateReport();
    });

    rightControls.add(this.sendBtn);
    controlsContainer.add(rightControls);

    // Initialize and load projects for both popovers
    this.#initializeAndLoadProjects();
    this.#updateModeBadgeUI();
  }

  async #initializeAndLoadProjects() {
    const sharingId = getSharingId();
    const projects = ProjectsState.projects;
    let targetOrgId = null;
    let shouldUpdateUserProjectType = false;
    let newProjectType = 'personal';

    // If we're on viewer page with a sharing ID, determine the correct organization context
    if (sharingId && CadFileState.cadfile) {
      const cadfile = CadFileState.cadfile;

      // Check if the task belongs to an organization
      if (
        cadfile.belongsToOrganization ||
        cadfile.model_folder?.organizationId
      ) {
        targetOrgId = cadfile.model_folder?.organizationId || null;
        newProjectType = 'team';

        // Check if user's current selection differs from the task's organization context
        const currentProjectsType =
          UserState.user?.selectedProjectsType || 'personal';
        const currentSelectedOrg = UserState.user?.selectedOrg;

        if (
          currentProjectsType !== 'team' ||
          currentSelectedOrg !== targetOrgId
        ) {
          shouldUpdateUserProjectType = true;
        }
      } else {
        // Task is personal, ensure we load personal projects
        targetOrgId = null;
        newProjectType = 'personal';

        const currentProjectsType =
          UserState.user?.selectedProjectsType || 'personal';
        if (currentProjectsType !== 'personal') {
          shouldUpdateUserProjectType = true;
        }
      }
    } else {
      // Not on viewer page, use user's current selection
      const selectedProjectsType =
        UserState.user?.selectedProjectsType || 'personal';
      targetOrgId =
        selectedProjectsType === 'team' ? UserState.user?.selectedOrg : null;
    }

    // Update user's project type selection if needed
    if (shouldUpdateUserProjectType) {
      try {
        const updateData = { selectedProjectsType: newProjectType };

        if (newProjectType === 'team' && targetOrgId) {
          updateData.selectedOrg = targetOrgId;
        }

        await editUserDetails(updateData);

        // Update the local user state
        UserState.setUser({
          ...UserState.user,
          ...updateData,
        });
      } catch (error) {
        console.error('Failed to update user project type selection:', error);
        // Continue with loading projects even if user update fails
      }
    }

    // Check if projects are already loaded for the target organization
    if (
      projects &&
      (projects.folders || projects.tasks) &&
      !projects.isLoading &&
      projects.rootFolderOrganizationId === targetOrgId
    ) {
      // Projects already loaded for the correct organization
      return;
    }

    try {
      // Load projects for the determined organization context
      await ProjectsState.loadProjects(targetOrgId);
    } catch (error) {
      console.error(
        'Failed to load projects in AiAnalyzerReportSection:',
        error,
      );
    }
  }

  #buildModeBadge() {
    this.modeBadgeContainer.clear();

    this.modeBadge = new UIDiv();
    this.modeBadge.addClass(
      'inline-flex items-center gap-1 rounded-full bg-slate-100 p-1',
    );

    this.modeButtons = new Map();
    [
      {
        mode: 'work',
        label: i18next.t('sidebar.aiAnalyzer.mode.work') || 'Workspace',
        icon: new AiSparkIcon({ size: 14, color: 'currentColor' }),
        color: 'text-blue-700',
      },
      {
        mode: 'detect',
        label: i18next.t('sidebar.aiAnalyzer.mode.detect') || 'Detect',
        icon: new UIIcon('scan-eye', {
          color: 'none',
          strokeColor: 'currentColor',
          size: 14,
        }),
        color: 'text-purple-700',
      },
    ].forEach(({ mode, label, icon, color }) => {
      const button = new UIButton();
      button.addClass(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ' +
          color +
          ' hover:' +
          color.replace('text-', 'hover:text-'),
      );
      button.add(icon, new UIText(label).setClass('whitespace-nowrap'));
      button.onClick(e => {
        e.stopPropagation();
        this.#setMode(mode);
      });
      this.modeButtons.set(mode, button);
      this.modeBadge.add(button);
    });

    this.modeBadgeContainer.add(this.modeBadge);
  }

  #setMode(mode) {
    if (!['work', 'chat', 'detect'].includes(mode)) return;
    this.currentMode = mode;
    this.#updateModeBadgeUI();
    if (this.onModeChange) this.onModeChange(this.currentMode);
  }

  #updateModeBadgeUI() {
    const isDetectMode = this.currentMode === 'detect';
    this.modeButtons?.forEach((button, mode) => {
      if (mode === this.currentMode) {
        button.removeClass('text-slate-500 hover:text-slate-700');
        button.addClass(
          mode === 'detect'
            ? 'bg-white text-[#2563EB] shadow-sm'
            : 'bg-white text-blue-700 shadow-sm',
        );
      } else {
        button.removeClass('bg-white text-blue-700 text-[#2563EB] shadow-sm');
        button.addClass('text-slate-500 hover:text-slate-700');
      }
    });

    this.setClass(
      isDetectMode
        ? 'relative flex flex-col border-t border-[rgba(0,0,0,0.08)] bg-white px-4 pt-3 pb-4 transition-all mb-0'
        : 'relative flex flex-col bg-white border border-gray-200 rounded-md space-y-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all mb-1',
    );
    this.modeBadgeContainer?.setClass(
      isDetectMode ? 'flex items-center mb-2.5' : 'px-3 pt-2',
    );
    this.modeBadge?.setClass(
      isDetectMode
        ? 'flex items-center gap-0.5 bg-[#F3F3F6] rounded-lg p-0.5'
        : 'inline-flex items-center gap-1 rounded-full bg-slate-100 p-1',
    );
    this.controlsContainer?.setClass(
      isDetectMode
        ? 'flex flex-col gap-2 mt-2.5 px-0 pb-0'
        : 'flex justify-between items-center mt-0 px-3 pb-2',
    );
    this.leftControls?.setClass(
      isDetectMode
        ? 'flex w-full items-start gap-2 min-w-0'
        : 'flex items-center gap-2',
    );
    this.rightControls?.setClass(
      isDetectMode
        ? 'flex w-full items-center justify-end gap-2'
        : 'flex items-center gap-2',
    );

    this.workspaceControls?.setClass(
      isDetectMode ? 'hidden' : 'flex items-center gap-2',
    );
    this.detectControls?.setClass(
      isDetectMode ? 'flex min-w-0 flex-1 flex-col gap-2' : 'hidden',
    );
    this.bookmarkButton?.setClass(
      isDetectMode
        ? 'hidden'
        : 'p-2 hover:bg-slate-100 rounded-full transition-colors',
    );
    this.historyButton?.setClass(
      isDetectMode
        ? 'inline-flex h-8 items-center justify-center px-2.5 rounded-md text-[#8888A0] hover:bg-[#F4F4F8] hover:text-[#555566] transition-colors border border-[rgba(0,0,0,0.08)]'
        : 'p-2 hover:bg-slate-100 rounded-full transition-colors',
    );
    this.textarea?.setClass(
      isDetectMode
        ? [
            'w-full min-h-[72px] px-3.5 pt-3 pb-2.5',
            'bg-transparent',
            'border-none outline-none focus:outline-none focus:ring-0 focus:border-none',
            'resize-none overflow-auto',
            'disabled:cursor-not-allowed',
            'max-h-[30vh]',
            'text-[13px] text-[#333344] placeholder:text-[#AAAABC] leading-relaxed',
          ].join(' ')
        : [
            'w-full py-2',
            'bg-white',
            'border-none outline-none focus:outline-none focus:ring-0 focus:border-none',
            'resize-none overflow-auto',
            'disabled:cursor-not-allowed',
            'max-h-[30vh]',
          ].join(' '),
    );

    if (isDetectMode) {
      this.textarea.setPlaceholder(
        this.#translatePlaceholder(
          'sidebar.aiAnalyzer.detect_placeholder',
          'Describe what to detect',
        ),
      );
    } else if (this.currentMode === 'chat') {
      this.textarea.setPlaceholder(
        this.#translatePlaceholder(
          'sidebar.aiAnalyzer.chat_placeholder',
          'Ask me anything...',
        ),
      );
    } else {
      this.textarea.setPlaceholder(
        this.#translatePlaceholder(
          'sidebar.aiAnalyzer.textarea_placeholder',
          'Type commands (/)',
        ),
      );
    }

    this.#updateDetectSelectionUI();
    this.#updateSendButtonState();
    this.#updateDetectSendButtonUI();
  }

  getMode() {
    return this.currentMode;
  }

  setMode(mode) {
    this.#setMode(mode);
  }

  getContext() {
    return this.taskPlugin ? this.taskPlugin.getContext() : [];
  }

  setContext(context) {
    if (this.taskPlugin) {
      this.taskPlugin.setContext(context);
    }
  }

  #hasEnoughCredits(minCredits = 2) {
    const credits = getCurrentSpaceCredits();
    return typeof credits === 'number' && credits >= minCredits;
  }

  #translatePlaceholder(key, fallback) {
    const value = i18next.t(key);
    return value && value !== key ? value : fallback;
  }

  getPrompt() {
    return this.textarea.getValue().trim();
  }

  setPrompt(prompt) {
    this.textarea.setValue(prompt);
    setTimeout(() => {
      this.#updateSendButtonState();
    }, 0);
  }

  clearPrompt() {
    this.textarea.setValue('');
    this.hasStartedTyping = false;
    setTimeout(() => {
      this.#updateSendButtonState();
      this.focusInput();
    }, 0);
  }

  setDisabled(isDisabled) {
    this.textarea.dom.contentEditable = !isDisabled;
    this.sendBtn?.setDisabled(isDisabled);
  }

  showLoading() {
    this.isLoading = true;
    this.arrowIcon.addClass('hidden');
    this.loadingSpinner.show();
    this.micButton?.setDisabled(true);
    this.sendBtn.setDisabled(true);
  }

  hideLoading() {
    this.isLoading = false;
    this.arrowIcon.removeClass('hidden');
    this.loadingSpinner.hide();
    this.micButton?.setDisabled(false);
    this.#updateSendButtonState();
  }

  focusInput() {
    if (!IS_APP()) this.textarea.dom.focus();
  }

  #updateFileCountUIValue() {
    const fileCount = FoldersState.getFileCount();
    this.fileCount.setValue(`${fileCount}`);
  }

  updateProjectCountUIValue() {
    const selectedItems = AiSelectedItems.selectedItems;
    const projectCount =
      selectedItems.projects.length + selectedItems.tasks.length;
    this.projectCount.setValue(`${projectCount}`);
  }

  #buildDetectControls() {
    this.detectControlsWrapper = new UIDiv();
    this.detectControlsWrapper.setClass('hidden flex-col gap-2 min-w-0');

    this.detectRunModeControl = this.#buildDetectRunModeControl();
    this.detectProviderControl = new SpatialVisionProviderSelector({
      compact: true,
    });
    this.detectTopBar = new UIDiv();
    this.detectTopBar.setClass(
      'flex w-full items-center justify-between gap-2',
    );
    this.detectTopBar.add(
      this.detectRunModeControl,
      // this.detectProviderControl,
    );

    this.detectSelectedImageChip = new UIDiv();
    this.detectSelectedImageChip.setClass('hidden');
    this.detectImageSourceControls = new UIDiv();
    this.detectImageSourceControls.setClass(
      'flex w-full flex-wrap items-center gap-2',
    );
    this.detectSequenceControl = new UIDiv();
    this.detectSequenceControl.setClass('hidden');

    this.detectUploadInput = new UIInput('', { defaultStyle: false });
    this.detectUploadInput
      .setType('file')
      .setAttribute(
        'accept',
        '.png,.jpg,.jpeg,.webp,.heic,.heif,image/png,image/jpeg,image/webp,image/heic,image/heif',
      )
      .setAttribute('multiple', 'multiple')
      .setClass('hidden');
    this.detectUploadInput.onChange(() => {
      const files = Array.from(this.detectUploadInput.dom.files || []);
      if (!files.length) return;
      const { accepted, rejected } = partitionVlmImages(files);
      const mappedImages = accepted.map(file => ({
        source: 'upload',
        file,
        originalName: file.name,
        fileSize: file.size,
      }));
      const currentImages = this.#getDetectImages();
      const images = this.#canSelectMultipleDetectImages()
        ? [...currentImages, ...mappedImages].slice(0, 10)
        : mappedImages.slice(0, 1);
      if (mappedImages.length) {
        this.setDetectSelection({
          ...this.getDetectSelection(),
          image: images[0] || null,
          images,
        });
      }
      if (rejected.length) {
        this.showDetectImageValidation(
          rejected,
          mappedImages.length ? images.length : currentImages.length,
        );
      } else this.clearDetectImageValidation();
      this.detectUploadInput.dom.value = '';
    });

    const uploadButton = new UIButton();
    uploadButton.addClass(
      'h-8 px-2.5 bg-white hover:bg-[#F4F4F8] border border-[rgba(0,0,0,0.1)] rounded-md text-[12px] text-[#555566] flex items-center gap-1.5 transition-colors',
    );
    uploadButton.setTitle(
      `Upload images (${VLM_MAX_IMAGE_SIZE_MB} MB maximum each)`,
    );
    uploadButton.add(
      new UIIcon('upload', {
        color: 'none',
        strokeColor: 'currentColor',
        size: 16,
      }),
      new UIText(
        i18next.t('sidebar.aiAnalyzer.detect.upload_image') || 'Upload image',
      ).setClass('whitespace-nowrap'),
    );
    uploadButton.onClick(e => {
      e.stopPropagation();
      this.detectUploadInput.dom.click();
    });

    const projectImageButton = new UIButton();
    projectImageButton.addClass(
      'h-8 px-2.5 bg-white hover:bg-[#F4F4F8] border border-[rgba(0,0,0,0.1)] rounded-md text-[12px] text-[#555566] flex items-center gap-1.5 transition-colors',
    );
    projectImageButton.add(
      new UIIcon('folder', {
        color: 'none',
        strokeColor: 'currentColor',
        size: 16,
      }),
      new UIText(
        i18next.t('sidebar.aiAnalyzer.detect.select_from_project') ||
          'Select from project',
      ).setClass('whitespace-nowrap'),
    );

    this.detectProjectPopoverContent = new AiAnalyzerDetectPopover({
      getSelection: () => this.getDetectSelection(),
      onChange: selection => {
        this.clearDetectImageValidation();
        this.setDetectSelection(selection);
        if (
          selection.image?.source === 'project' &&
          !this.#canSelectMultipleDetectImages()
        ) {
          Popover.hide();
        }
      },
    });
    projectImageButton.onClick(e => {
      e.stopPropagation();
      this.#showDetectProjectPopover(projectImageButton);
    });

    this.detectImageSourceControls.add(
      this.detectUploadInput,
      uploadButton,
      projectImageButton,
      new UIText(`${VLM_MAX_IMAGE_SIZE_MB} MB max per image`).setClass(
        'self-center text-[11px] text-[#9999AA] whitespace-nowrap',
      ),
    );

    this.detectImageValidationMessage = new UIDiv();
    this.detectImageValidationMessage.setClass('hidden');
    this.detectImageValidationMessage.setAttribute('role', 'status');
    this.detectImageValidationMessage.setAttribute('aria-live', 'polite');

    this.detectControlsWrapper.add(
      this.detectTopBar,
      this.detectSequenceControl,
      this.detectSelectedImageChip,
      this.detectImageSourceControls,
      this.detectImageValidationMessage,
    );

    return this.detectControlsWrapper;
  }

  showDetectImageValidation(rejected, keptCount = 0) {
    if (!this.detectImageValidationMessage) return;
    const notice = oversizedVlmImageNotice(rejected, keptCount);
    this.detectImageValidationMessage.clear();
    this.detectImageValidationMessage.setClass(
      'flex w-full items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-900',
    );
    const copy = new UIDiv()
      .setClass('min-w-0 flex-1')
      .add(
        new UIText(notice.title).setClass(
          'block text-[12px] font-semibold leading-snug',
        ),
        new UIText(notice.description).setClass(
          'mt-0.5 block text-[11px] leading-snug text-amber-800',
        ),
      );
    this.detectImageValidationMessage.add(
      new UIText('!').setClass(
        'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white',
      ),
      copy,
      new UIButton()
        .setClass(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded text-[16px] leading-none text-amber-700 hover:bg-amber-100 hover:text-amber-900',
        )
        .setTitle('Dismiss image size message')
        .setAttribute('aria-label', 'Dismiss image size message')
        .add(new UIText('×'))
        .onClick(event => {
          event.stopPropagation();
          this.clearDetectImageValidation();
        }),
    );
  }

  clearDetectImageValidation() {
    if (!this.detectImageValidationMessage) return;
    this.detectImageValidationMessage.clear();
    this.detectImageValidationMessage.setClass('hidden');
  }

  #showDetectProjectPopover(trigger) {
    Popover.show({
      trigger,
      content: this.detectProjectPopoverContent,
      placement: 'top-end',
      hideOnSelect: false,
    });
  }

  #buildDetectRunModeControl() {
    const wrapper = new UIDiv().setClass(
      'inline-flex items-center gap-0.5 rounded-lg bg-[#F3F3F6] p-0.5',
    );

    this.detectPromptModeButton = new UIButton('Prompt');
    this.detectPromptModeButton.onClick(e => {
      e.stopPropagation();
      this.setDetectSelection({
        ...this.getDetectSelection(),
        runMode: 'direct',
        sequenceId: null,
        sequenceName: null,
      });
    });

    this.detectSequenceModeButton = new UIButton('Sequence');
    this.detectSequenceModeButton.onClick(e => {
      e.stopPropagation();
      const selection = this.getDetectSelection();
      const sequences = SpatialVisionState.spatialVision.sequences || [];
      const sequence =
        sequences.find(
          item => String(item.id) === String(selection.sequenceId),
        ) || sequences[0];
      this.setDetectSelection({
        ...selection,
        runMode: 'sequence',
        sequenceId: sequence?.id || null,
        sequenceName: sequence?.name || null,
      });
    });

    wrapper.add(this.detectPromptModeButton, this.detectSequenceModeButton);
    return wrapper;
  }

  #updateDetectRunModeButtons() {
    const isSequenceMode = this.detectSelection?.runMode === 'sequence';
    [
      [this.detectPromptModeButton, !isSequenceMode],
      [this.detectSequenceModeButton, isSequenceMode],
    ].forEach(([button, active]) => {
      if (!button) return;
      button.setClass(
        [
          'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
          active
            ? 'bg-white text-[#2563EB] shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
            : 'text-[#8888A0] hover:text-[#555566]',
        ].join(' '),
      );
    });
  }

  #updateDetectSelectionUI() {
    const selectedImages = this.#getDetectImages();
    const imageName =
      selectedImages.length > 1
        ? `${selectedImages.length} images`
        : selectedImages[0]?.originalName;
    const sequenceName = this.detectSelection?.sequenceName;
    const isSequenceMode = this.detectSelection?.runMode === 'sequence';
    const hasUnsupportedBatch = this.#hasUnsupportedDetectBatch();

    this.#updateDetectRunModeButtons();
    this.detectImageSourceControls?.setClass(
      this.currentMode === 'detect'
        ? 'flex w-full flex-wrap items-center gap-2'
        : 'hidden',
    );
    this.#renderDetectImageChip(imageName);
    this.#renderDetectSequenceControl(isSequenceMode, sequenceName);

    if (this.currentMode === 'detect') {
      this.textareaContainer?.setClass(
        isSequenceMode
          ? 'hidden'
          : 'relative min-h-[92px] flex-grow mx-0 rounded-xl border border-[rgba(0,0,0,0.08)] bg-[#F8F8FC] focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-[#2563EB]/10 transition-all',
      );
    } else {
      this.textareaContainer?.setClass('relative flex-grow px-3');
    }
    if (hasUnsupportedBatch) {
      this.detectSequenceMessage?.setClass(
        'mx-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700',
      );
      this.detectSequenceMessage?.setTextContent(
        'O-HIVE VLM PRO can process only one image at a time. Switch to O-HIVE VLM CLOUD or keep one image selected.',
      );
    } else {
      this.detectSequenceMessage?.setClass(
        this.currentMode === 'detect' && isSequenceMode
          ? 'px-3 py-2 text-sm text-slate-500'
          : 'hidden',
      );
    }
  }

  #renderDetectImageChip(imageName) {
    if (!this.detectSelectedImageChip) return;
    this.detectSelectedImageChip.clear();
    const selectedImages = this.#getDetectImages();
    if (!imageName || selectedImages.length === 0) {
      this.detectSelectedImageChip.setClass('hidden');
      return;
    }

    this.detectSelectedImageChip.setClass(
      'flex w-full items-center gap-2 overflow-x-auto rounded-lg border border-[rgba(0,0,0,0.08)] bg-[#F8F8FC] px-2 py-2',
    );
    selectedImages.forEach((image, index) => {
      const imageUrl = this.#getDetectImagePreviewUrl(image);
      const thumb = new UIDiv().setClass(
        'relative shrink-0 h-9 w-12 overflow-hidden rounded-md border border-[rgba(0,0,0,0.1)] bg-[#F0F0F4] group',
      );
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = image.originalName || `Image ${index + 1}`;
        img.title = image.originalName || `Image ${index + 1}`;
        img.className = 'h-full w-full object-cover';
        thumb.dom.appendChild(img);
      } else {
        thumb.add(
          new UIIcon('image', {
            color: 'none',
            strokeColor: '#AAAABC',
            size: 16,
          }),
        );
      }
      thumb.add(
        new UIButton()
          .setClass(
            'absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#333344] text-white opacity-0 transition-opacity group-hover:opacity-100',
          )
          .setTitle('Remove image')
          .add(
            new UIIcon('circle-cross', {
              color: 'none',
              strokeColor: 'currentColor',
              size: 10,
            }),
          )
          .onClick(e => {
            e.stopPropagation();
            const images = selectedImages.filter((_, i) => i !== index);
            this.setDetectSelection({
              ...this.getDetectSelection(),
              image: images[0] || null,
              images,
            });
          }),
      );
      this.detectSelectedImageChip.add(thumb);
    });

    this.detectSelectedImageChip.add(
      new UIText(imageName).setClass(
        'shrink-0 text-[11px] font-medium text-[#8888A0]',
      ),
    );
    this.detectSelectedImageChip.dom.onclick = e => {
      e.stopPropagation();
      this.#showDetectProjectPopover(this.detectSelectedImageChip);
    };
  }

  #renderDetectSequenceControl(isSequenceMode) {
    if (!this.detectSequenceControl) return;
    this.detectSequenceControl.clear();
    this.detectSequenceControl.setClass(isSequenceMode ? 'block' : 'hidden');
    if (!isSequenceMode) return;

    const sequences = SpatialVisionState.spatialVision.sequences || [];
    if (!sequences.length) {
      this.detectSequenceControl.add(
        new UIButton('Create sequence')
          .setClass(
            'h-8 rounded-md border border-blue-200 bg-blue-50 px-3 text-[12px] font-medium text-blue-700 hover:bg-blue-100 transition-colors',
          )
          .onClick(e => {
            e.stopPropagation();
            route('/spatial-vision');
          }),
      );
      this.#updateDetectSequenceMessage();
      return;
    }

    const select = new UISelect();
    select.setClass(
      'h-9 w-full rounded-lg border border-[rgba(0,0,0,0.1)] bg-white px-3 text-[12px] text-[#555566] outline-none focus:ring-2 focus:ring-blue-500',
    );

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Choose a sequence';
    placeholder.disabled = true;
    select.dom.appendChild(placeholder);

    sequences.forEach(sequence => {
      const option = document.createElement('option');
      option.value = sequence.id;
      option.textContent = sequence.name || 'Untitled sequence';
      select.dom.appendChild(option);
    });

    select.setValue(this.detectSelection.sequenceId || '');
    select.dom.addEventListener('change', () => {
      const sequenceId = select.getValue() || null;
      const sequence = sequences.find(
        item => String(item.id) === String(sequenceId),
      );
      this.setDetectSelection({
        ...this.getDetectSelection(),
        runMode: 'sequence',
        sequenceId,
        sequenceName: sequence?.name || null,
      });
    });

    this.detectSequenceControl.add(select);
    this.#updateDetectSequenceMessage();
  }

  #updateDetectSequenceMessage() {
    if (!this.detectSequenceMessage) return;
    const selectedImages = this.#getDetectImages();
    const imageText =
      selectedImages.length > 1
        ? `${selectedImages.length} images`
        : selectedImages[0]?.originalName;
    const sequenceName = this.detectSelection?.sequenceName;
    const sequences = SpatialVisionState.spatialVision.sequences || [];
    let message = 'Select one or more images, then choose a sequence to run.';

    if (!sequences.length) {
      message =
        'Create a sequence in Spatial Vision before running sequence detection.';
    } else if (!imageText) {
      message = 'Select one or more images, then choose a sequence to run.';
    } else if (sequenceName) {
      message = `Ready to run "${sequenceName}" on ${imageText}.`;
    } else {
      message = 'Choose a sequence above to run on the selected images.';
    }

    this.detectSequenceMessage.setTextContent(message);
  }

  #canSelectMultipleDetectImages() {
    return (
      this.detectSelection?.runMode === 'sequence' ||
      SpatialVisionState.spatialVision.selectedServiceTier === 'cloud'
    );
  }

  #hasUnsupportedDetectBatch() {
    return (
      SpatialVisionState.spatialVision.selectedServiceTier !== 'cloud' &&
      this.detectSelection?.runMode !== 'sequence' &&
      this.#getDetectImages().length > 1
    );
  }

  #getDetectImages() {
    if (this.detectSelection?.images?.length > 0) {
      return this.detectSelection.images;
    }
    return this.detectSelection?.image ? [this.detectSelection.image] : [];
  }

  #getDetectImagePreviewUrl(image) {
    if (!image) return null;
    if (image.previewUrl) return image.previewUrl;
    if (image.path) return image.path;
    if (image.file) {
      image.previewUrl = URL.createObjectURL(image.file);
      return image.previewUrl;
    }
    return null;
  }

  getDetectSelection() {
    return {
      image: this.detectSelection?.image || null,
      images: this.#getDetectImages(),
      runMode: this.detectSelection?.runMode || 'direct',
      sequenceId: this.detectSelection?.sequenceId || null,
      sequenceName: this.detectSelection?.sequenceName || null,
    };
  }

  setDetectSelection(selection = {}) {
    const runMode =
      selection.runMode || (selection.sequenceId ? 'sequence' : 'direct');
    const images =
      selection.images?.length > 0
        ? selection.images
        : selection.image
          ? [selection.image]
          : [];
    const normalizedImages = images;
    this.detectSelection = {
      runMode,
      image: normalizedImages[0] || null,
      images: normalizedImages,
      sequenceId: selection.sequenceId || null,
      sequenceName: selection.sequenceName || null,
    };
    this.#updateDetectSelectionUI();
    this.#updateSendButtonState();
    this.detectProjectPopoverContent?.refreshSelection?.();
    this.onDetectSelectionChange?.(this.getDetectSelection());
  }

  #buildProjectPopover() {
    // Add file selection indicator
    const projectIndicator = new UIButton();
    projectIndicator.addClass(
      'p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm text-slate-600 flex items-center gap-1 transition-colors',
    );

    const projectIcon = new ProjectsIcon({
      size: 16,
      color: 'none',
      strokeColor: 'black',
    });
    projectIndicator.add(projectIcon);

    this.projectCount = new UIText(0);
    projectIndicator.add(this.projectCount);

    const popoverContent = new AiAnalyzerProjectsPopover();
    projectIndicator.onClick(e => {
      e.stopPropagation();
      popoverContent.initializeViewFromSelections();
      // Open the project
      Popover.show({
        trigger: projectIndicator,
        content: popoverContent,
        placement: 'top-end',
        hideOnSelect: false,
      });
    });
    return projectIndicator;
  }

  #updateSendButtonState() {
    if (!this.textarea || !this.sendBtn) return;

    const prompt = this.textarea.getValue().trim();
    if (this.currentMode === 'detect') {
      const hasImage = Boolean(this.detectSelection?.image);
      const isSequenceMode = this.detectSelection?.runMode === 'sequence';
      const hasRunnableIntent = isSequenceMode
        ? Boolean(this.detectSelection?.sequenceId)
        : prompt !== '';
      this.sendBtn.setDisabled(
        !(hasImage && hasRunnableIntent) || this.#hasUnsupportedDetectBatch(),
      );
      this.#updateDetectSendButtonUI();
      return;
    }

    if (prompt === '') {
      this.sendBtn.setDisabled(true);
    } else {
      this.sendBtn.setDisabled(false);
    }
    this.#updateDetectSendButtonUI();
  }

  #updateDetectSendButtonUI() {
    if (!this.sendBtn || !this.sendLabel) return;
    if (this.currentMode === 'detect') {
      const isSequenceMode = this.detectSelection?.runMode === 'sequence';
      this.sendBtn.setClass(
        [
          'flex h-9 w-9 items-center justify-center rounded-full transition-colors shadow-sm',
          isSequenceMode
            ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'
            : 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]',
          'disabled:opacity-40 disabled:cursor-not-allowed',
        ].join(' '),
      );
      this.arrowIcon?.setClass(this.isLoading ? 'hidden h-5 w-5' : 'h-5 w-5');
      this.sendLabel.addClass('hidden');
      return;
    }

    this.sendBtn.setClass(
      [
        'p-2 bg-[var(--o-hive-primary-80)] text-white rounded-full',
        'hover:bg-[var(--o-hive-primary-100)] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors',
        'disabled:bg-gray-400 disabled:cursor-not-allowed',
      ].join(' '),
    );
    this.arrowIcon?.setClass(this.isLoading ? 'hidden' : '');
    this.sendLabel.addClass('hidden');
  }

  findCommand(prompt, { localOnly = false } = {}) {
    // Get all available AI commands
    const commands = this.aiPlugin.getAICommands();

    // Check if the prompt contains any command, optionally filter by local
    for (const command of commands) {
      if (
        (!localOnly || command.local) &&
        prompt.toLowerCase().includes(command.command.toLowerCase())
      ) {
        return command;
      }
    }

    return null;
  }

  //**
  // MICROPHONE FUNCTIONS
  //  */

  #checkSpeechRecognitionSupport() {
    // Check if the API exists
    if (
      !('webkitSpeechRecognition' in window) &&
      !('SpeechRecognition' in window)
    ) {
      return false;
    }

    // Detect browsers that don't properly support Web Speech API
    const userAgent = navigator.userAgent;

    // iOS Chrome doesn't properly support Web Speech API
    const isIOSChrome =
      /CriOS/i.test(userAgent) && /iPad|iPhone|iPod/.test(userAgent);

    // Firefox doesn't support Web Speech API
    const isFirefox = /Firefox/i.test(userAgent);

    // Opera has limited support
    const isOpera = /OPR|Opera/i.test(userAgent);

    // Exclude all iOS devices for the moment due to issue with the app, and not using the IS_IOS for that
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

    if (isIOSChrome || isFirefox || isOpera || isIOS) {
      return false;
    }

    return true;
  }

  #initializeSpeechRecognition() {
    // Use the appropriate constructor
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    // Test if SpeechRecognition actually works
    try {
      const testRecognition = new SpeechRecognition();
      // Quick test to see if the API is functional
      testRecognition.onerror = event => {
        if (
          event.error === 'not-allowed' ||
          event.error === 'service-not-allowed'
        ) {
          // These are expected permission errors, API works
          return;
        }
        // API doesn't work properly
        console.warn('Speech recognition API test failed:', event.error);
        this.micButton?.addClass('hidden');
        return;
      };

      this.recognition = new SpeechRecognition();
    } catch (error) {
      console.warn('Failed to create SpeechRecognition instance:', error);
      this.micButton?.addClass('hidden');
      return;
    }

    // Configure speech recognition
    const isIOSSafari =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !window.MSStream &&
      !navigator.userAgent.includes('CriOS') &&
      !navigator.userAgent.includes('FxiOS');
    const isDesktop =
      !/Android|iPad|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    this.recognition.continuous = true;
    // Enable interim results for desktop and iOS Safari
    this.recognition.interimResults = isDesktop || isIOSSafari;

    // Set language based on current i18next language
    const languageToLocale = {
      en: 'en-US',
      kr: 'ko-KR',
    };
    this.recognition.lang = languageToLocale[i18next.language] || 'en-US';

    // Store the text before speech recognition started
    this.speechStartText = '';
    this.lastProcessedResultIndex = 0;
    this.speechUpdateTimeout = null;

    // Store browser compatibility info
    this.isIOSSafari =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !window.MSStream &&
      !navigator.userAgent.includes('CriOS') &&
      !navigator.userAgent.includes('FxiOS');
    this.isDesktop =
      !/Android|iPad|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
    this.shouldEnableContinuous = true; // Enable for all devices
    this.shouldEnableInterim = this.isDesktop || this.isIOSSafari;

    // Handle speech recognition results
    this.recognition.onresult = event => {
      // Clear any pending update timeout
      if (this.speechUpdateTimeout) {
        clearTimeout(this.speechUpdateTimeout);
        this.speechUpdateTimeout = null;
      }

      let interimTranscript = '';
      let finalTranscript = '';

      // Process only new results to avoid duplication on mobile
      for (
        let i = this.lastProcessedResultIndex;
        i < event.results.length;
        i++
      ) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
          this.lastProcessedResultIndex = i + 1;
        } else if (this.shouldEnableInterim) {
          // Only show interim results on iOS Safari
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        // Append final transcript to the original text
        const newValue =
          this.speechStartText +
          (this.speechStartText ? ' ' : '') +
          finalTranscript.trim();
        this.textarea.setValue(newValue);
        this.speechStartText = newValue; // Update the base text
        this.textarea.removeClass('speech-interim');
        this.#updateSendButtonState();

        // Stop recognition for browsers without continuous mode
        if (!this.shouldEnableContinuous) {
          this.recognition.stop();
        }
      } else if (interimTranscript && this.shouldEnableInterim) {
        // Show interim transcript only on iOS Safari with debouncing
        this.speechUpdateTimeout = setTimeout(() => {
          const newValue =
            this.speechStartText +
            (this.speechStartText ? ' ' : '') +
            interimTranscript.trim();
          this.textarea.setValue(newValue);

          // Add visual feedback that this is interim text
          this.textarea.addClass('speech-interim');
          this.#updateSendButtonState();
        }, 100); // 100ms debounce
      }
    };

    // Handle speech recognition start
    this.recognition.onstart = () => {
      this.isRecording = true;
      this.speechStartText = this.textarea.getValue().trim();
      this.lastProcessedResultIndex = 0; // Reset result index
      this.#updateMicButtonState();
    };

    // Handle speech recognition end
    this.recognition.onend = () => {
      this.isRecording = false;
      this.textarea.removeClass('speech-interim');
      this.#updateMicButtonState();

      // Clear any pending update timeout
      if (this.speechUpdateTimeout) {
        clearTimeout(this.speechUpdateTimeout);
        this.speechUpdateTimeout = null;
      }
    };

    // Handle speech recognition errors
    this.recognition.onerror = event => {
      console.error('Speech recognition error:', event.error);

      // Clear any pending update timeout
      if (this.speechUpdateTimeout) {
        clearTimeout(this.speechUpdateTimeout);
        this.speechUpdateTimeout = null;
      }

      // Handle the error
      toast.error(event.error);
      this.#handleSpeechError(event.error);
    };
  }

  #handleSpeechError(errorType) {
    this.isRecording = false;
    this.textarea.removeClass('speech-interim');
    this.#updateMicButtonState();

    // Show user-friendly error messages
    if (errorType === 'not-allowed') {
      toast.error(
        i18next.t('sidebar.aiAnalyzer.voice_recognition.error_access_denied'),
      );
    } else if (errorType === 'no-speech') {
      toast.error(
        i18next.t('sidebar.aiAnalyzer.voice_recognition.error_no_speech'),
      );
    } else if (errorType === 'network') {
      toast.error(
        i18next.t('sidebar.aiAnalyzer.voice_recognition.error_network'),
      );
    } else {
      toast.error(
        i18next.t(
          'sidebar.aiAnalyzer.voice_recognition.error_recognition_failed',
        ),
      );
    }
  }

  #toggleSpeechRecognition() {
    if (!this.recognition) {
      toast.error(
        i18next.t('sidebar.aiAnalyzer.voice_recognition.error_not_supported'),
      );
      return;
    }
    if (IS_IOS()) {
      if (this.iOSRecording) {
        this.iOSRecording = false;
        this.#updateMicButtonState();
        if (window.webkit?.messageHandlers?.stopRecording) {
          window.webkit.messageHandlers.stopRecording.postMessage('clicked');
        }
      } else {
        this.iOSRecording = true;
        this.#updateMicButtonState();
        if (window.webkit?.messageHandlers?.startRecording) {
          window.webkit.messageHandlers.startRecording.postMessage('clicked');
        }
      }
    } else {
      if (this.isRecording) {
        this.recognition.stop();
      } else {
        try {
          this.recognition.start();
        } catch (error) {
          console.error('Failed to start speech recognition:', error);
          toast.error(
            i18next.t(
              'sidebar.aiAnalyzer.voice_recognition.error_start_failed',
            ),
          );
        }
      }
    }
  }

  #updateMicButtonState() {
    if (this.isRecording || (IS_IOS() && this.iOSRecording)) {
      // Change visual state to indicate recording
      this.micButton.removeClass('hover:bg-slate-100');
      this.micPing.removeClass('hidden');
      this.micButton.addClass('ring-blue-500 hover:ring-blue-200');
      this.micButton.setAttribute(
        'title',
        i18next.t('sidebar.aiAnalyzer.voice_recognition.mic_tooltip_recording'),
      );
    } else {
      // Reset to normal state
      this.micButton.removeClass('ring-blue-500 hover:ring-blue-200');
      this.micPing.addClass('hidden');
      this.micButton.addClass('hover:bg-slate-100');
      this.micButton.setAttribute(
        'title',
        i18next.t('sidebar.aiAnalyzer.voice_recognition.mic_tooltip_start'),
      );
    }
  }

  // Cleanup method to stop recording when component is destroyed or hidden
  stopSpeechRecognition() {
    if (this.recognition && this.isRecording) {
      this.recognition.stop();
    }
    this.textarea.removeClass('speech-interim');

    // Clear any pending update timeout
    if (this.speechUpdateTimeout) {
      clearTimeout(this.speechUpdateTimeout);
      this.speechUpdateTimeout = null;
    }
  }
}

export { AiAnalyzerReportSection };
